import React from 'react';
import { useNavigate } from 'react-router';
import type { BrandConfig } from '@lds-efsp/brand-config';
import { CA_COUNTIES as COUNTIES_REF } from '@lds-efsp/shared-contracts';
import { useOrderForm } from '../contexts/OrderFormContext';
import DocumentUploader from '../components/DocumentUploader';
import OrderSummaryPanel from '../components/OrderSummaryPanel';
import PricingEstimate from '../components/PricingEstimate';
import FormInput, { FormSelect } from '../../setup/components/FormInput';

const countyOptions = [
  { value: '', label: 'Select county...' },
  ...COUNTIES_REF.map(c => ({ value: c.name, label: c.name })),
];

interface CountyRecordingFormProps {
  brand: BrandConfig;
}

const CountyRecordingForm: React.FC<CountyRecordingFormProps> = ({ brand }) => {
  const navigate = useNavigate();
  const {
    priority,
    typeData,
    documents,
    updateTypeData,
    uploadDocument,
    removeDocument,
    submitOrder,
  } = useOrderForm();

  const data = typeData as { state?: string; county?: string; jurisdictionId?: string };

  return (
    <div className="flex gap-6">
      <div className="flex-1 space-y-6">
        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            County Recording Details
          </h3>
          <FormInput
            brand={brand}
            label="State"
            name="state"
            value={data.state ?? 'CA'}
            onChange={(e) => updateTypeData({ state: e.target.value } as Partial<typeof typeData>)}
            disabled
          />
          <FormSelect
            brand={brand}
            label="County"
            name="county"
            value={data.county ?? ''}
            onChange={(e) => updateTypeData({ county: e.target.value } as Partial<typeof typeData>)}
            options={countyOptions}
            required
          />
          <FormInput
            brand={brand}
            label="Jurisdiction ID"
            name="jurisdictionId"
            value={data.jurisdictionId ?? ''}
            onChange={(e) => updateTypeData({ jurisdictionId: e.target.value } as Partial<typeof typeData>)}
            required
          />
          <PricingEstimate brand={brand} county={data.county ?? ''} priority={priority} />
        </div>

        <div className="border rounded-lg p-4" style={{ borderColor: brand.colors.border }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: brand.colors.text }}>
            Documents
          </h3>
          <DocumentUploader
            brand={brand}
            documents={documents}
            onUpload={uploadDocument}
            onRemove={removeDocument}
          />
        </div>
      </div>

      <div className="w-80 flex-shrink-0 hidden lg:block">
        <OrderSummaryPanel
          brand={brand}
          onSubmit={async () => {
            try {
              await submitOrder();
              navigate('/orders');
            } catch { /* Error displayed in panel */ }
          }}
        />
      </div>
    </div>
  );
};

export default CountyRecordingForm;
